import React, { useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import { logAdminAction } from '../../lib/adminAuth'
import { isMasterAdminViewSession } from '../../lib/masterAdminAccess'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface ResolvedUser {
  id: number
  username: string | null
  full_name: string | null
  email: string | null
  availableShares: number
  kycStatus: string | null
}

interface Props { currentUser?: any }

const callApi = async (operation: string, data: Record<string, any>) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/share-transfer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ operation, data })
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok || j?.success === false) throw new Error(j?.error || `Request failed (${r.status})`)
  return j
}

export const AdminShareTransfer: React.FC<Props> = ({ currentUser }) => {
  const readOnly = isMasterAdminViewSession()
  const adminEmail = currentUser?.adminUser?.email || currentUser?.email || ''

  const [senderInput, setSenderInput] = useState('')
  const [recipientInput, setRecipientInput] = useState('')
  const [sender, setSender] = useState<ResolvedUser | null>(null)
  const [recipient, setRecipient] = useState<ResolvedUser | null>(null)
  const [shares, setShares] = useState<string>('')
  const [reason, setReason] = useState<string>('')

  const [lookupBusy, setLookupBusy] = useState<{ sender: boolean; recipient: boolean }>({ sender: false, recipient: false })
  const [transferBusy, setTransferBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const lookup = async (kind: 'sender' | 'recipient') => {
    const identifier = (kind === 'sender' ? senderInput : recipientInput).trim()
    if (!identifier) return
    setError(null); setSuccess(null)
    setLookupBusy(prev => ({ ...prev, [kind]: true }))
    try {
      const j = await callApi('lookup_user', { identifier })
      const u = j.user as ResolvedUser
      if (kind === 'sender') setSender(u); else setRecipient(u)
    } catch (e: any) {
      setError(e.message || 'Lookup failed')
      if (kind === 'sender') setSender(null); else setRecipient(null)
    } finally {
      setLookupBusy(prev => ({ ...prev, [kind]: false }))
    }
  }

  const submit = async () => {
    if (readOnly || transferBusy) return
    setError(null); setSuccess(null)

    if (!sender || !recipient) { setError('Look up both sender and recipient first.'); return }
    if (sender.id === recipient.id) { setError('Sender and recipient must differ.'); return }

    const sharesNum = Number(shares)
    if (!sharesNum || sharesNum <= 0) { setError('Enter a positive share amount.'); return }
    if (sharesNum > sender.availableShares) {
      setError(`Sender only has ${sender.availableShares} shares available.`); return
    }
    const trimmedReason = reason.trim()
    if (trimmedReason.length < 3) { setError('Provide a reason (min 3 characters) for the audit log.'); return }

    const recipientKycComplete = recipient.kycStatus === 'completed' || recipient.kycStatus === 'approved' || recipient.kycStatus === 'verified'
    const kycWarning = recipientKycComplete ? '' :
      `\n\n⚠️  RECIPIENT KYC IS "${recipient.kycStatus || 'missing'}".\nThis admin transfer will BYPASS the KYC gate. The bypass will be permanently\nrecorded in share_transfers.transfer_metadata and admin_audit_logs.`

    const ok = window.confirm(
      `Transfer ${sharesNum} shares\n\n` +
      `FROM: ${sender.username || `user ${sender.id}`} (${sender.email || '—'})\n` +
      `TO:   ${recipient.username || `user ${recipient.id}`} (${recipient.email || '—'})\n\n` +
      `Reason: ${trimmedReason}\n\n` +
      `This will NOT generate any commissions.${kycWarning}\n\nContinue?`
    )
    if (!ok) return

    setTransferBusy(true)
    try {
      const j = await callApi('transfer', {
        senderId: sender.id,
        recipientId: recipient.id,
        shares: sharesNum,
        reason: trimmedReason
      })
      setSuccess(`Transfer complete. ID: ${j.transferId}. Sender balance after: ${j.senderBalanceAfter}.`)

      try {
        await logAdminAction(adminEmail, 'admin_share_transfer', 'share_transfers', String(j.transferId), {
          sender_user_id: sender.id, recipient_user_id: recipient.id,
          shares_transferred: sharesNum, reason: trimmedReason, no_commissions_generated: true
        })
      } catch (e) { console.warn('logAdminAction failed (non-fatal):', e) }

      // Refresh sender/recipient balances after transfer
      try {
        const [s2, r2] = await Promise.all([
          callApi('lookup_user', { identifier: String(sender.id) }),
          callApi('lookup_user', { identifier: String(recipient.id) })
        ])
        setSender(s2.user); setRecipient(r2.user)
      } catch {}

      setShares(''); setReason('')
    } catch (e: any) {
      setError(e.message || 'Transfer failed')
    } finally {
      setTransferBusy(false)
    }
  }

  const renderUserCard = (label: string, u: ResolvedUser | null) => (
    <div className="bg-gray-900/60 border border-gray-700 rounded p-3 text-sm min-h-[88px]">
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">{label}</div>
      {u ? (
        <>
          <div className="text-white font-semibold">{u.username || `user ${u.id}`} <span className="text-gray-400 font-normal">#{u.id}</span></div>
          <div className="text-gray-300 text-xs">{u.full_name || '—'} · {u.email || '—'}</div>
          <div className="mt-1 text-yellow-400">Available shares: <strong>{u.availableShares}</strong></div>
          <div className="text-xs text-gray-400">KYC: {u.kycStatus || 'missing'}</div>
        </>
      ) : <div className="text-gray-500 italic">Not yet looked up</div>}
    </div>
  )

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-white">🔁 Admin Share Transfer</h2>
      </div>

      <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 mb-4 text-blue-200 text-sm">
        Transfers shares directly from one user to another. <strong>No commissions are generated</strong> by this operation —
        an atomic admin RPC moves share ownership only, with no referral cascade.
        Recipient KYC is <strong>not required</strong>; any bypass is permanently recorded in the audit log
        and on the transfer row's metadata.
      </div>

      {readOnly && <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mb-4 text-yellow-300 text-sm">🔒 Read-only mode — share transfers disabled in master admin view.</div>}
      {error && <div className="bg-red-900/20 border border-red-500/30 rounded p-3 mb-4 text-red-300 text-sm">{error}</div>}
      {success && <div className="bg-green-900/20 border border-green-500/30 rounded p-3 mb-4 text-green-300 text-sm">{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Sender (id, username, or email)</label>
          <div className="flex gap-2">
            <input
              type="text" value={senderInput} onChange={e => setSenderInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') lookup('sender') }}
              className="flex-1 bg-gray-900 border border-gray-600 text-white rounded px-3 py-2 text-sm"
              placeholder="e.g. 123 or alice or alice@example.com"
            />
            <button
              onClick={() => lookup('sender')} disabled={lookupBusy.sender || !senderInput.trim()}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white text-sm px-3 py-2 rounded"
            >{lookupBusy.sender ? '…' : 'Look up'}</button>
          </div>
          <div className="mt-2">{renderUserCard('Sender', sender)}</div>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Recipient (id, username, or email)</label>
          <div className="flex gap-2">
            <input
              type="text" value={recipientInput} onChange={e => setRecipientInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') lookup('recipient') }}
              className="flex-1 bg-gray-900 border border-gray-600 text-white rounded px-3 py-2 text-sm"
              placeholder="e.g. 456 or bob or bob@example.com"
            />
            <button
              onClick={() => lookup('recipient')} disabled={lookupBusy.recipient || !recipientInput.trim()}
              className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white text-sm px-3 py-2 rounded"
            >{lookupBusy.recipient ? '…' : 'Look up'}</button>
          </div>
          <div className="mt-2">{renderUserCard('Recipient', recipient)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Shares to transfer</label>
          <input
            type="number" min="0" step="0.01" value={shares}
            onChange={e => setShares(e.target.value)}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded px-3 py-2 text-sm"
            placeholder="e.g. 100"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-300 mb-1">Reason (audit log)</label>
          <input
            type="text" value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded px-3 py-2 text-sm"
            placeholder="e.g. Correcting purchase misallocation, ticket #1234"
          />
        </div>
      </div>

      <button
        onClick={submit}
        disabled={readOnly || transferBusy || !sender || !recipient}
        className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold px-6 py-2 rounded"
      >
        {transferBusy ? 'Transferring…' : 'Transfer Shares'}
      </button>
    </div>
  )
}

export default AdminShareTransfer
